import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../services/api';
import { isAllowedConnectUrl, mailErrorKind, providerCompany, providerName } from './mailUtils';

export const CONNECT_POLL_MS = 3000;
export const CONNECT_POLL_LIMIT_MS = 3 * 60 * 1000;
export const PROGRESS_POLL_MS = 1500;
export const PROGRESS_POLL_LIMIT_MS = 3 * 60 * 1000;

export function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!intervalMs) return undefined;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// phase: idle | opening | waiting | connected | timeout | error; `app` is the Composio toolkit (gmail | outlook).
export function useGmailConnect({ connectUrl, connectionId, onConnected, app = 'gmail' } = {}) {
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const [activeApp, setActiveApp] = useState(app);
  const appRef = useRef(app);
  // Only the connection minted for this attempt counts; an older linked grant of the same app does not.
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const checkingRef = useRef(false);
  const pollingRef = useRef(false);
  const onConnectedRef = useRef(onConnected);

  useEffect(() => { onConnectedRef.current = onConnected; }, [onConnected]);

  const stop = useCallback(() => {
    pollingRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const check = useCallback(async () => {
    if (!pollingRef.current || checkingRef.current) return;
    if (Date.now() - startedAtRef.current > CONNECT_POLL_LIMIT_MS) {
      stop();
      setPhase('timeout');
      return;
    }
    checkingRef.current = true;
    try {
      const polled = appRef.current;
      const pending = pendingRef.current;
      const res = await (pending ? api.getComposioStatus(polled, pending) : api.getComposioStatus(polled));
      if (pollingRef.current && polled === appRef.current && res?.connected) {
        stop();
        setPhase('connected');
        onConnectedRef.current?.(polled);
      }
    } catch {
      // Transient status failures keep polling until the time limit.
    } finally {
      checkingRef.current = false;
    }
  }, [stop]);

  const beginPolling = useCallback(() => {
    stop();
    pollingRef.current = true;
    startedAtRef.current = Date.now();
    setPhase('waiting');
    timerRef.current = setInterval(check, CONNECT_POLL_MS);
  }, [check, stop]);

  useEffect(() => {
    const onFocus = () => { check(); };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      stop();
    };
  }, [check, stop]);

  const fail = useCallback((msg) => {
    stop();
    setError(msg);
    setPhase('error');
  }, [stop]);

  // Called as an onClick handler too, so a non-string argument means "the hook's own app".
  const start = useCallback(async (chosen) => {
    const target = typeof chosen === 'string' ? chosen : app;
    if (target !== appRef.current) stop();
    appRef.current = target;
    setActiveApp(target);
    setError(null);
    const badLink = `This sign-in link doesn't look like ${providerCompany(target)}'s, so we didn't open it.`;
    pendingRef.current = null;
    if (connectUrl && target === app) {
      if (!isAllowedConnectUrl(connectUrl)) { fail(badLink); return; }
      pendingRef.current = connectionId || null;
      window.open(connectUrl, '_blank', 'noopener');
      beginPolling();
      return;
    }
    // Open synchronously inside the click so popup blockers allow it; navigate once the URL arrives.
    const popup = window.open('', '_blank');
    setPhase('opening');
    let url = null;
    try {
      const res = await api.composioConnect(target);
      url = res?.redirect_url || null;
      pendingRef.current = typeof res?.connection_id === 'string' && res.connection_id ? res.connection_id : null;
    } catch {
      popup?.close();
      fail(`Couldn't start the ${providerName(target)} connection. Try again.`);
      return;
    }
    if (!isAllowedConnectUrl(url)) {
      popup?.close();
      fail(badLink);
      return;
    }
    if (popup) {
      popup.opener = null;
      popup.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
    beginPolling();
  }, [app, connectUrl, connectionId, beginPolling, fail, stop]);

  return { phase, error, start, checkNow: check, app: activeApp };
}

// state: idle | loading | ok | expired | auth | disabled | error
export function useMailDraft(draftId, enabled = true) {
  const active = Boolean(enabled && draftId);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState({ key: null, state: 'loading', draft: null });
  const key = active ? `${draftId}:${reloadKey}` : null;

  useEffect(() => {
    if (!key) return undefined;
    const controller = new AbortController();
    api.getMailDraft(draftId, { signal: controller.signal })
      .then((draft) => {
        if (!controller.signal.aborted) setResult({ key, state: 'ok', draft: draft || {} });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        const kind = mailErrorKind(err);
        setResult({ key, state: kind === 'not_found' ? 'expired' : kind, draft: null });
      });
    return () => controller.abort();
  }, [key, draftId]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  if (!active) return { state: 'idle', draft: null, reload };
  if (result.key !== key) return { state: 'loading', draft: null, reload };
  return { state: result.state, draft: result.draft, reload };
}

export function useSyncProgress(active) {
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    let timer = null;
    const controller = new AbortController();
    const startedAt = Date.now();
    const tick = async () => {
      let keepPolling = true;
      try {
        const res = await api.getMailSyncProgress({ signal: controller.signal });
        if (!alive) return;
        setProgress(res && typeof res === 'object' ? res : null);
      } catch (err) {
        if (!alive) return;
        setProgress(null);
        if (mailErrorKind(err) === 'disabled') keepPolling = false;
      }
      if (alive && keepPolling && Date.now() - startedAt < PROGRESS_POLL_LIMIT_MS) {
        timer = setTimeout(tick, PROGRESS_POLL_MS);
      }
    };
    tick();
    return () => {
      alive = false;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [active]);

  return active ? progress : null;
}
